/**
 * Topic Trends.
 *
 * A **Topic** is a saved semantic query that counts meetings over time for a chart. It is a
 * SEPARATE OBJECT from a Signal, which finds documents for a feed. They share a creation
 * sheet and nothing else — separate collections, separate management surfaces ("Set Topics"
 * is not "Manage signals"). Do not merge them.
 *
 * Grounded in `mv_topic_trends` in Atlas, which already aggregates topic prevalence per city
 * per month. The chart reads a materialized view; it does not compute anything live.
 *
 * Every stat below derives from the pipeline's real `vote` field:
 *   non-null  the item was voted on
 *   null      the item was discussed, no vote taken
 * Never infer a vote outcome from item type or summary text.
 */

export interface Topic {
  id: string;
  name: string;
  /** The semantic query behind it. */
  terms: string;
}

/** One month's bar in the 12-month chart. */
export interface MonthPoint {
  /** "Jul 2026" — the one accepted place for a month-name abbreviation. */
  label: string;
  meetings: number;
}

export interface CityCount {
  city: string;
  region: string;
  meetings: number;
}

/**
 * The four stat cards. Each one drills into a real, dated list of the meetings behind it —
 * this was a specific fix: drill-downs must show per-topic content, not one hardcoded dataset
 * reused across every topic filter.
 */
export type StatKey = 'cities' | 'votedOn' | 'contested' | 'noVote';

export const STAT_META: Record<StatKey, { label: string; hint: string }> = {
  cities: { label: 'Cities discussing', hint: 'At least one meeting mentioning this topic' },
  votedOn: { label: 'Voted on', hint: 'Items with a recorded vote' },
  contested: { label: 'Contested votes', hint: 'Recorded votes that were not unanimous' },
  noVote: { label: 'No vote taken', hint: 'Discussed, but no vote recorded' },
};

export interface TopicTrends {
  topicId: string;
  months: MonthPoint[];
  stats: Record<StatKey, number>;
  topCities: CityCount[];
  /** State/province distribution, descending. */
  byRegion: { region: string; meetings: number }[];
}

/** One row of a drill-down: the underlying meeting, dated and attributable. */
export interface TopicMeeting {
  meetingId: string;
  city: string;
  region: string;
  /** mm/dd/yyyy */
  date: string;
  itemType: string | null;
  num: string | null;
  /** Read from `vote`; null means discussed with no vote taken. */
  vote: string | null;
  excerpt: string;
}

/** True where a recorded vote was not unanimous. Contested is a property of the tally. */
export function isContested(vote: string | null): boolean {
  if (!vote) return false;
  return !/unanimous/i.test(vote);
}

export function chartMax(months: MonthPoint[]): number {
  return Math.max(1, ...months.map((m) => m.meetings));
}
